import { useState } from "react";
import { useTable, useSortBy } from "react-table";
import React from "react";

// icons — matching CashAdvance icon set exactly
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import VisibilityIcon from "@mui/icons-material/Visibility";   // View
import EditNoteIcon from "@mui/icons-material/EditNote";     // Edit
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";  // Cancel
import PreviewIcon from "@mui/icons-material/Preview";      // History
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"; // Set Loan Detail
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import { generateSARPDF } from "./generateSARPDF";

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const colors = {
    Pending: { bg: "#fff3cd", color: "#856404" },
    Approved: { bg: "#d1e7dd", color: "#0a3622" },
    Processing: { bg: "#cfe2ff", color: "#084298" },
    Disapproved: { bg: "#f8d7da", color: "#842029" },
    Cancelled: { bg: "#e2e3e5", color: "#41464b" },
    Completed: { bg: "#d1e7dd", color: "#0a3622" },
    Ongoing: { bg: "#d1e7dd", color: "#0a3622" }, // ADDED
  };
  const style = colors[status] ?? { bg: "#e2e3e5", color: "#41464b" };

  return (
    <span style={{
      backgroundColor: style.bg,
      color: style.color,
      borderRadius: "12px",
      padding: "2px 10px",
      fontSize: "11px",
      fontWeight: "600",
      whiteSpace: "nowrap",
    }}>
      {status}
    </span>
  );
}

// ── Tooltip — identical to CashAdvance ───────────────────────────────────────
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-block", zIndex: "1" }}>
      <div onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
        {children}
      </div>
      {show && (
        <div style={{
          position: "absolute",
          top: "80%",
          left: "-50%",
          transform: "translateX(-50%)",
          backgroundColor: "white",
          color: "gray",
          padding: "5px",
          borderRadius: "5px",
          pointerEvents: "none",
          boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2), 0 6px 20px 0 rgba(0,0,0,0.19)",
          whiteSpace: "nowrap",
          fontSize: "12px",
        }}>
          {text}
        </div>
      )}
    </div>
  );
}

// ── Per-SAR action icons ──
function SarActions({ sar, userAccess, onView, onSetLoanDetail, onCancel, onHistory, onActivate, onRelease }) {
  const isPending = sar.status === "Pending";
  const isApproved = sar.status === "Approved";
  const isProcessing = sar.status === "Processing";
  const isOngoing = sar.status === "Ongoing";
  const isCompleted = sar.status === "Completed";

  const isAssignmentManager = userAccess?.isSalaryAdvanceAssignmentManager;
  const isPreparerManager = userAccess?.isSalaryAdvancePreparerManager;
  const isReleasingManager = userAccess?.isSalaryAdvanceReleasingManager;

  const canEdit = isPending;
  const canSetLoan = (isApproved || isProcessing) && isPreparerManager;
  const canCancel = isPending || isApproved || isProcessing;
  const canSetLoanStatus = isProcessing && isReleasingManager;

  const isFullLoanAccess = userAccess?.isFullLoanAccess;

  return (
    <div
      className="row"
      style={{ alignItems: "center", justifyContent: "center", margin: "30px 0" }}
    >
      <span style={{ fontSize: "12px", color: "#555", marginRight: "8px" }}>
        {`${sar.referenceNumber} (₱${Number(sar.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}) - `}
        <StatusBadge status={sar.status} />
      </span>

      {/* View */}
      <Tooltip text="View">
        <div className="history" style={{ padding: "0 5px", margin: "0 5px", cursor: "pointer" }}
          onClick={() => onView(sar)}>
          <VisibilityIcon />
        </div>
      </Tooltip>

      {/* Set Loan Detail — Approved or Processing */}
      {(canSetLoan && isFullLoanAccess) && (
        <Tooltip text={isProcessing ? "Edit Draft Terms" : "Set Loan Detail"}>
          <div className="recycle" style={{ padding: "0 5px", marginRight: "5px", cursor: "pointer" }}
            onClick={() => onSetLoanDetail(sar)}>
            <AccountBalanceIcon style={{ color: isProcessing ? "#084298" : "inherit" }} />
          </div>
        </Tooltip>
      )}

      {(canSetLoanStatus && isFullLoanAccess && sar?.isReleased) && (
        <Tooltip text={"Start Loan"}>
          <div className="edit" style={{ padding: "0 5px", marginRight: "5px", cursor: "pointer" }}
            onClick={() => onActivate(sar)}>
            <PlayCircleOutlineIcon style={{ color: "#2e7d32" }} />
          </div>
        </Tooltip>
      )}

      {(canSetLoanStatus && isFullLoanAccess && !sar?.isReleased) && (
        <Tooltip text={"Release Loan"}>
          <div className="recycle" style={{ padding: "0 5px", marginRight: "5px", cursor: "pointer" }}
            onClick={() => onRelease(sar)}>
            <SendIcon style={{ color: "#f0b152" }} />
          </div>
        </Tooltip>
      )}

      {/* Cancel — Pending, Approved, or Processing */}
      {(canCancel && isFullLoanAccess) && (
        <Tooltip text="Cancel">
          <div className="delete" style={{ padding: "0 5px", marginRight: "5px", cursor: "pointer" }}
            onClick={() => onCancel(sar)}>
            <DeleteSweepIcon />
          </div>
        </Tooltip>
      )}

      {/* History — always visible */}
      <Tooltip text="History">
        <div className="history" style={{ padding: "0 5px", marginRight: "5px", cursor: "pointer" }}
          onClick={() => onHistory(sar)}>
          <PreviewIcon />
        </div>
      </Tooltip>

      {/* --- NEW PDF BUTTON --- */}
      {((isApproved || isOngoing || isProcessing || isCompleted) && isFullLoanAccess) && (
        <Tooltip text="Download PDF Form">
          <div
            className="edit"
            style={{ padding: "0 5px", marginRight: "5px", cursor: "pointer", color: "#d32f2f" }}
            onClick={() => generateSARPDF(sar)}
          >
            <PictureAsPdfIcon />
          </div>
        </Tooltip>
      )}

    </div>
  );
}

// ── Main exported table ───────────────────────────────────────────────────────
export function SalaryAdvanceTable({
  sarList,
  userAccess,
  currentPage,
  setCurrentPage,
  onView,
  onAdd,
  onEdit,
  onSetLoanDetail,
  onCancel,
  onHistory,
  onActivate, // ADDED
  onRelease,
}) {
  const ITEMS_PER_PAGE = 10;
  const headers = ["Employee ID", "Employee", ""];
  const accessors = ["employeeIdBadge", "employeeFullName", "sarDetails"];

  const [sortHeaderValue, setSortHeaderValue] = useState({ id: "", desc: false, isDefault: true });

  const columns = React.useMemo(
    () => headers.map((h, i) => ({ Header: h, accessor: accessors[i] })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setSortBy } =
    useTable({ columns, data: sarList, initialState: { sortBy: [] } }, useSortBy);

  const totalPages = Math.ceil(rows.length / ITEMS_PER_PAGE);
  const indexFirst = (currentPage - 1) * ITEMS_PER_PAGE;
  const indexLast = Math.min(currentPage * ITEMS_PER_PAGE, rows.length);
  const currentItems = rows.slice(indexFirst, indexLast);
  const isLastPage = currentPage === totalPages;

  const paginate = (n) => { if (n >= 1 && n <= totalPages) setCurrentPage(n); };

  const sortStates = [{ textValue: "" }, { textValue: "↑" }, { textValue: "↓" }];

  const headerClick = (column) => {
    if (sortHeaderValue.id !== column.id) {
      setSortHeaderValue({ id: column.id, desc: false, isDefault: false });
      setSortBy([{ id: column.id, desc: false }]);
    } else if (sortHeaderValue.isDefault) {
      setSortHeaderValue({ id: column.id, desc: false, isDefault: false });
      setSortBy([{ id: column.id, desc: false }]);
    } else if (!sortHeaderValue.desc) {
      setSortHeaderValue({ id: column.id, desc: true, isDefault: false });
      setSortBy([{ id: column.id, desc: true }]);
    } else {
      setSortHeaderValue({ id: "", desc: false, isDefault: true });
      setSortBy([]);
    }
  };

  return (
    <div>
      <table {...getTableProps()} className="border-collapse">
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps()} onClick={() => headerClick(col)} className="sortable-header">
                  {`${col.render("Header")} ${sortHeaderValue.id === col.id
                    ? sortStates[sortHeaderValue.isDefault ? 0 : sortHeaderValue.desc ? 2 : 1].textValue
                    : ""
                    }`}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {sarList.length === 0 && (
            <tr>
              <td colSpan={3} style={{ textAlign: "center", padding: "20px", color: "#888" }}>
                No salary advance requests found.
              </td>
            </tr>
          )}

          {currentItems.map((row) => {
            prepareRow(row);
            const emp = row.original;
            return (
              <tr key={emp.employeePersonalInformationId} {...row.getRowProps()}>

                {/* Employee ID */}
                <td style={{ verticalAlign: "top", paddingTop: "38px", width: "120px" }}>
                  {emp.employeeIdBadge}
                </td>

                {/* Employee Name + Department */}
                <td style={{ verticalAlign: "top", paddingTop: "38px", width: "200px" }}>
                  <div>{emp.employeeFullName}</div>
                  {emp.department && (
                    <div style={{ fontSize: "11px", color: "#888" }}>{emp.department}</div>
                  )}
                </td>

                {/* Actions */}
                <td>
                  {emp.sarDetails.map((sar) => (
                    <SarActions
                      key={sar.id}
                      userAccess={userAccess}
                      sar={sar}
                      onView={onView}
                      onSetLoanDetail={onSetLoanDetail}
                      onCancel={onCancel}
                      onHistory={onHistory}
                      onActivate={onActivate} // ADDED
                      onRelease={onRelease}
                    />
                  ))}
                </td>

              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {rows.length > ITEMS_PER_PAGE && (
        <div>
          <div className="table-page row" style={{ alignItems: "center", justifyContent: "end", padding: "20px 0 0 0" }}>
            <button className="prev row" onClick={() => paginate(1)} disabled={currentPage === 1}
              style={{ visibility: currentPage === 1 ? "hidden" : "visible", marginRight: "5px", height: "26px" }}>
              &lt;&lt;
            </button>
            <button className="prev row" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}
              style={{ visibility: currentPage === 1 ? "hidden" : "visible", marginRight: "5px" }}>
              <NavigateBeforeIcon style={{ marginRight: "5px" }} /> PREVIOUS
            </button>
            <button className="next row" onClick={() => paginate(currentPage + 1)} disabled={indexLast >= rows.length}
              style={{ visibility: rows.length > ITEMS_PER_PAGE && !isLastPage ? "visible" : "hidden", marginRight: "5px" }}>
              NEXT <NavigateNextIcon style={{ marginLeft: "5px" }} />
            </button>
            <button className="next row" onClick={() => paginate(totalPages)} disabled={indexLast >= rows.length}
              style={{ visibility: rows.length > ITEMS_PER_PAGE && !isLastPage ? "visible" : "hidden", height: "26px" }}>
              &gt;&gt;
            </button>
          </div>
          <span>{`${currentPage}/${totalPages}`}</span>
        </div>
      )}
    </div>
  );
}